import { useCallback, useMemo } from 'react';

import { useSearchParams } from './useSearchParams';

export type SampleType = 'multi' | 'body' | 'face';
export type MainSample = 'face' | 'body';

export interface EventInfoSettings {
  isVisibleDate: boolean;
  isVisibleSource: boolean;
  isVisibleAddress: boolean;
  isVisibleLists: boolean;
  sampleType: SampleType;
  mainSample: MainSample;
}

type EventInfoSettingName = keyof EventInfoSettings;

const DEFAULT_SETTINGS = {
  isVisibleDate: true,
  isVisibleSource: true,
  isVisibleAddress: true,
  isVisibleLists: true,
  sampleType: 'multi',
  mainSample: 'face',
} satisfies EventInfoSettings;

const PARAMS = {
  isVisibleDate: 'date',
  isVisibleSource: 'source',
  isVisibleAddress: 'address',
  isVisibleLists: 'lists',
  sampleType: 'sample',
  mainSample: 'main',
} as const;

interface UseEventInfoSettingsLocationReturn {
  eventInfoSettings: EventInfoSettings;
  setEventInfoSetting: <Name extends EventInfoSettingName>(
    name: Name,
    value: EventInfoSettings[Name],
  ) => void;
  resetEventInfoSettings: () => void;
}

function getBooleanParam(
  searchParams: URLSearchParams,
  paramName: string,
  defaultValue: boolean,
): boolean {
  const value = searchParams.get(paramName);

  if (value === null) return defaultValue;

  return value === '1';
}

export function useEventInfoSettingsLocation(): UseEventInfoSettingsLocationReturn {
  const { searchParams, replaceSearchParams } = useSearchParams();

  const eventInfoSettings = useMemo<EventInfoSettings>(() => ({
    isVisibleDate: getBooleanParam(searchParams, PARAMS.isVisibleDate, DEFAULT_SETTINGS.isVisibleDate),
    isVisibleSource: getBooleanParam(searchParams, PARAMS.isVisibleSource, DEFAULT_SETTINGS.isVisibleSource),
    isVisibleAddress: getBooleanParam(searchParams, PARAMS.isVisibleAddress, DEFAULT_SETTINGS.isVisibleAddress),
    isVisibleLists: getBooleanParam(searchParams, PARAMS.isVisibleLists, DEFAULT_SETTINGS.isVisibleLists),
    sampleType: (searchParams.get(PARAMS.sampleType) as SampleType | null) ?? DEFAULT_SETTINGS.sampleType,
    mainSample: (searchParams.get(PARAMS.mainSample) as MainSample | null) ?? DEFAULT_SETTINGS.mainSample,
  }), [searchParams]);

  const setEventInfoSetting = useCallback(
    <Name extends EventInfoSettingName>(
      name: Name,
      value: EventInfoSettings[Name],
    ): void => {
      const nextParams = new URLSearchParams(searchParams);
      const paramName = PARAMS[name];

      if (value === DEFAULT_SETTINGS[name]) {
        nextParams.delete(paramName);
      } else if (typeof value === 'boolean') {
        nextParams.set(paramName, value ? '1' : '0');
      } else {
        nextParams.set(paramName, value);
      }

      replaceSearchParams(nextParams);
    },
    [replaceSearchParams, searchParams],
  );

  const resetEventInfoSettings = useCallback((): void => {
    const nextParams = new URLSearchParams(searchParams);

    Object.values(PARAMS).forEach((param) => {
      nextParams.delete(param);
    });

    replaceSearchParams(nextParams);
  }, [replaceSearchParams, searchParams]);

  return {
    eventInfoSettings,
    setEventInfoSetting,
    resetEventInfoSettings,
  };
}
